import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, deleteUser } from 'firebase/auth';
import { db, auth } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import staffService from '../../services/staffService';
import StaffModal from '../../components/brand/StaffModal';
import { Pencil, Trash2 } from 'lucide-react';

// Reusable toggle switch
const Toggle = ({ checked, onChange, disabled, label, description }) => (
  <div className="flex items-center justify-between py-3">
    <div className="pr-4">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      {description && <p className="text-sm text-gray-500">{description}</p>}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${
        checked ? 'bg-primary' : 'bg-gray-200'
      }`}
    >
      <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  </div>
);

const TABS = [
  { id: 'notifications', label: 'Notifications' },
  { id: 'security', label: 'Security & Login' },
  { id: 'privacy', label: 'Privacy & Data' },
  { id: 'staff', label: 'System Users' },
];

const DEFAULT_SETTINGS = {
  notifications: {
    emailCampaignUpdates: true,
    emailApplications: true,
    emailContentSubmissions: true,
    emailPayments: true,
    emailMarketing: false,
    pushCampaignUpdates: true,
    pushApplications: true,
    pushContentSubmissions: true,
    pushPayments: true,
    deadline3DaysBefore: true,
    deadline1DayBefore: true,
    applicationDeadlineReminder: true,
    contentReviewOverdue: true,
  },
  privacy: {
    publicProfile: true,
    showInDirectory: true,
    allowInfluencerInquiries: true,
    showCampaignBudget: false,
  },
};

const Settings = () => {
  const { currentUser, logout } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('notifications');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  // Staff state
  const [staffMembers, setStaffMembers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [availablePermissions, setAvailablePermissions] = useState([]);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Account deletion state
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch settings
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setSettings({
            notifications: { ...DEFAULT_SETTINGS.notifications, ...(userData.settings?.notifications || {}) },
            privacy: { ...DEFAULT_SETTINGS.privacy, ...(userData.settings?.privacy || {}) },
          });
        }

        // Fetch staff members
        const permissions = staffService.getAvailablePermissions();
        setAvailablePermissions(permissions);

        if (currentUser?.uid) {
          const staffData = await staffService.getStaffByBrandId(currentUser.uid);
          setStaffMembers(staffData);
        }
      } catch (err) {
        setError('Failed to load settings. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [currentUser]);

  const handleNotificationChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      notifications: { ...prev.notifications, [key]: value },
    }));
  };

  const handlePrivacyChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      privacy: { ...prev.privacy, [key]: value },
    }));
  };

  const handleSaveSettings = async () => {
    if (!currentUser) return;
    setSaving(true);
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        settings,
        updatedAt: new Date(),
      });
      showSuccess('Settings saved successfully.', 'Your preferences have been updated.', 3000);
    } catch (err) {
      showError('Failed to save settings.', 'Please check your connection and try again.', 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!currentUser || !auth.currentUser) {
      showError('Authentication error.', 'Please log in again and try.', 5000);
      return;
    }

    if (newPassword.length < 6) {
      showError('Password too short.', 'New password must be at least 6 characters.', 4000);
      return;
    }
    if (newPassword !== confirmPassword) {
      showError('Passwords do not match.', 'Please make sure the new password and confirmation match.', 4000);
      return;
    }

    setChangingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);

      showSuccess('Password updated successfully.', 'You may need to log in again with your new password.', 5000);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const message = err.code === 'auth/wrong-password'
        ? 'Current password is incorrect. Please try again.'
        : err.code === 'auth/too-many-requests'
        ? 'Too many attempts. Please try again later.'
        : 'Failed to update password. Please try again.';
      showError('Password change failed.', 'Please try again or contact support if the problem persists.', 5000);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDownloadData = async () => {
    if (!currentUser) return;
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const data = userDoc.data();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `account-data-${currentUser.uid}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showSuccess('Data download started.', 'Your account data has been exported.', 3000);
      } else {
        showError('No data found.', 'Your account data could not be found.', 4000);
      }
    } catch (err) {
      showError('Failed to download data.', 'Please try again later.', 5000);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      showError('Confirmation required.', 'Please type "DELETE" to confirm account deletion.', 4000);
      return;
    }

    setDeletingAccount(true);
    try {
      if (!auth.currentUser) {
        showError('Authentication error.', 'Please log in again and try.', 5000);
        return;
      }

      // Delete user document from Firestore
      const userDocRef = doc(db, 'users', currentUser.uid);
      await deleteDoc(userDocRef);

      // Delete Firebase Auth account
      await deleteUser(auth.currentUser);

      showSuccess('Account deleted.', 'Your account has been permanently deleted.', 5000);
      await logout();
    } catch (err) {
      const message = err.code === 'auth/requires-recent-login'
        ? 'For security, please log out and log back in before deleting your account.'
        : 'Failed to delete account. Please try again or contact support.';
      showError('Account deletion failed.', 'Please try again or contact support if the problem persists.', 5000);
    } finally {
      setDeletingAccount(false);
      setShowDeleteModal(false);
    }
  };

  // Staff handlers
  const handleAddStaff = async (staffData) => {
    try {
      setError(null);
      const newStaff = await staffService.addStaffMember(currentUser.uid, staffData);
      const newStaffId = newStaff.id;
      const updatedStaff = await staffService.getStaffByBrandId(currentUser.uid);
      setStaffMembers(updatedStaff);
      return newStaffId;
    } catch (err) {
      setError('Failed to add staff member. Please try again.');
      return null;
    }
  };

  const handleEditStaff = async (staffId, staffData) => {
    try {
      setLoading(true);
      setError(null);
      if (!currentUser?.uid) return;
      await staffService.updateStaffMember(currentUser.uid, staffId, staffData);
      const updatedStaffData = await staffService.getStaffByBrandId(currentUser.uid);
      setStaffMembers(updatedStaffData);
      setEditingStaff(null);
    } catch (err) {
      setError('Failed to update staff member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;
    try {
      setLoading(true);
      setError(null);
      if (!currentUser?.uid) return;
      await staffService.deleteStaffMember(currentUser.uid, staffId);
      const updatedStaffData = await staffService.getStaffByBrandId(currentUser.uid);
      setStaffMembers(updatedStaffData);
    } catch (err) {
      setError('Failed to delete staff member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  if (error && activeTab === 'staff') {
    // Only show full-page error for staff tab; other tabs have their own error handling
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Settings</h2>
        <p className="text-gray-600">Manage your account preferences and system users</p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Email Notifications</h3>
                <div className="divide-y divide-gray-100">
                  <Toggle label="Campaign Updates" description="Get notified when campaign statuses change or campaigns go live" checked={settings.notifications.emailCampaignUpdates} onChange={(v) => handleNotificationChange('emailCampaignUpdates', v)} />
                  <Toggle label="Applications" description="Receive email alerts when influencers apply to your campaigns" checked={settings.notifications.emailApplications} onChange={(v) => handleNotificationChange('emailApplications', v)} />
                  <Toggle label="Content Submissions" description="Get email notifications when content is submitted for review" checked={settings.notifications.emailContentSubmissions} onChange={(v) => handleNotificationChange('emailContentSubmissions', v)} />
                  <Toggle label="Payments" description="Get email notifications for payment updates and processing" checked={settings.notifications.emailPayments} onChange={(v) => handleNotificationChange('emailPayments', v)} />
                  <Toggle label="Marketing & Tips" description="Receive platform updates, tips, and promotional content" checked={settings.notifications.emailMarketing} onChange={(v) => handleNotificationChange('emailMarketing', v)} />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Push Notifications</h3>
                <div className="divide-y divide-gray-100">
                  <Toggle label="Campaign Updates" description="In-app notifications for campaign status changes" checked={settings.notifications.pushCampaignUpdates} onChange={(v) => handleNotificationChange('pushCampaignUpdates', v)} />
                  <Toggle label="Applications" description="In-app notifications for new applications" checked={settings.notifications.pushApplications} onChange={(v) => handleNotificationChange('pushApplications', v)} />
                  <Toggle label="Content Submissions" description="In-app notifications for content submissions" checked={settings.notifications.pushContentSubmissions} onChange={(v) => handleNotificationChange('pushContentSubmissions', v)} />
                  <Toggle label="Payments" description="In-app notifications for payment updates" checked={settings.notifications.pushPayments} onChange={(v) => handleNotificationChange('pushPayments', v)} />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Deadline Reminders</h3>
                <div className="divide-y divide-gray-100">
                  <Toggle label="3 Days Before Campaign End" description="Get notified 3 days before your campaigns end" checked={settings.notifications.deadline3DaysBefore} onChange={(v) => handleNotificationChange('deadline3DaysBefore', v)} />
                  <Toggle label="1 Day Before Campaign End" description="Get notified 1 day before your campaigns end" checked={settings.notifications.deadline1DayBefore} onChange={(v) => handleNotificationChange('deadline1DayBefore', v)} />
                  <Toggle label="Application Deadline Reminders" description="Get notified before application deadlines close" checked={settings.notifications.applicationDeadlineReminder} onChange={(v) => handleNotificationChange('applicationDeadlineReminder', v)} />
                  <Toggle label="Content Review Overdue" description="Get reminded about overdue content reviews" checked={settings.notifications.contentReviewOverdue} onChange={(v) => handleNotificationChange('contentReviewOverdue', v)} />
                </div>
              </div>

              <div className="flex justify-end">
                <button type="button" onClick={handleSaveSettings} disabled={saving} className="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {saving ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            </div>
          )}

          {/* Security & Login Tab */}
          {activeTab === 'security' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Account Information</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-500">Email</span>
                    <span className="text-sm font-medium text-gray-900">{currentUser?.email}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-500">Email Verified</span>
                    <span className={`text-sm font-medium ${currentUser?.emailVerified ? 'text-green-600' : 'text-amber-600'}`}>
                      {currentUser?.emailVerified ? 'Verified' : 'Not verified'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-500">User ID</span>
                    <span className="text-sm font-mono text-gray-400">{currentUser?.uid?.slice(0, 8)}...</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Change Password</h3>
                <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                    <input type="password" id="currentPassword" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out" placeholder="Enter your current password" />
                  </div>
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                    <input type="password" id="newPassword" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength="6" className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out" placeholder="At least 6 characters" />
                  </div>
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                    <input type="password" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength="6" className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out" placeholder="Re-enter your new password" />
                  </div>
                  <div>
                    <button type="submit" disabled={changingPassword} className="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      {changingPassword ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>
                </form>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Danger Zone</h3>
                <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 pr-4">
                      <p className="text-sm font-medium text-gray-900">Delete Account</p>
                      <p className="text-sm text-gray-500 mt-1">Permanently delete your brand account, campaigns, and all associated data. This action cannot be undone.</p>
                    </div>
                    <button type="button" onClick={() => setShowDeleteModal(true)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors whitespace-nowrap">
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Privacy & Data Tab */}
          {activeTab === 'privacy' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Profile Visibility</h3>
                <div className="divide-y divide-gray-100">
                  <Toggle label="Public Profile" description="Allow influencers to view your brand profile" checked={settings.privacy.publicProfile} onChange={(v) => handlePrivacyChange('publicProfile', v)} />
                  <Toggle label="Show in Directory" description="Appear in the brand directory for influencer discovery" checked={settings.privacy.showInDirectory} onChange={(v) => handlePrivacyChange('showInDirectory', v)} />
                  <Toggle label="Allow Influencer Inquiries" description="Let influencers contact you directly for collaborations" checked={settings.privacy.allowInfluencerInquiries} onChange={(v) => handlePrivacyChange('allowInfluencerInquiries', v)} />
                  <Toggle label="Show Campaign Budget" description="Display budget information on public campaign listings" checked={settings.privacy.showCampaignBudget} onChange={(v) => handlePrivacyChange('showCampaignBudget', v)} />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Your Data</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Download Your Data</p>
                      <p className="text-sm text-gray-500">Export a copy of your account data as JSON</p>
                    </div>
                    <button type="button" onClick={handleDownloadData} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                      Download
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button type="button" onClick={handleSaveSettings} disabled={saving} className="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {saving ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            </div>
          )}

          {/* Staff Tab */}
          {activeTab === 'staff' && (
            <div>
              {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Staff Members</h3>
                  <p className="text-sm text-gray-500 mt-1">Manage system users and their permissions for your brand account.</p>
                </div>
                <button onClick={() => setShowAddModal(true)} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
                  Add Staff
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {staffMembers.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500">
                          No staff members found. Add your first staff member to get started.
                        </td>
                      </tr>
                    ) : (
                      staffMembers.map((staff) => (
                        <tr key={staff.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{staff.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{staff.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{staff.designation}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${staff.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {staff.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button onClick={() => setEditingStaff(staff)} className="text-primary hover:text-primary-dark mr-3">
                              <Pencil className="h-5 w-5" />
                            </button>
                            <button onClick={() => handleDeleteStaff(staff.id)} className="text-red-600 hover:text-red-900">
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {showAddModal && (
                <StaffModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onSave={handleAddStaff} availablePermissions={availablePermissions} brandId={currentUser?.uid} />
              )}
              {editingStaff && (
                <StaffModal isOpen={!!editingStaff} onClose={() => setEditingStaff(null)} onSave={handleEditStaff} staff={editingStaff} availablePermissions={availablePermissions} brandId={currentUser?.uid} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Account Deletion Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Account</h3>
            <p className="text-sm text-gray-500 mb-4">
              This will permanently delete your brand account, all campaigns, payment records, and associated data. This action <strong>cannot be undone</strong>.
            </p>
            <div className="mb-4">
              <label htmlFor="deleteConfirm" className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm
              </label>
              <input
                type="text"
                id="deleteConfirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-150 ease-in-out"
                placeholder="DELETE"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }} className="inline-flex justify-center py-2.5 px-6 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button type="button" onClick={handleDeleteAccount} disabled={deletingAccount || deleteConfirmText !== 'DELETE'} className="inline-flex justify-center py-2.5 px-6 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {deletingAccount ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
